<template>
  <ErrorScreen
    v-if="
      !isReconnectingScreenVisible &&
        (isAppUnpublished ||
          authorizationFailedReason ||
          networkStatus === 'disconnected')
    "
    :reason="
      authorizationFailedReason ??
        (isAppUnpublished ? 'unpublished' : 'disconnected')
    "
  />

  <template v-else>
    <main
      v-if="isReady"
      :class="{
        'app-main': true,
        frameless:
          appType === ClientAppType.embedded
            ? !!config.embedded?.frameless
            : false,
        'kiosk-landscape-30inch': isKioskType,
        'kiosk-portrait-30inch': appType === ClientAppType.kioskPortrait,
        'embedded-client': appType === ClientAppType.embedded
      }"
    >
      <template v-if="jwt || networkStatus === 'shutdown'">
        <div
          v-if="config.envMode === 'staging'"
          class="mdr-test-buttons-container"
        >
          {{ prevEndpointStatusCode }} / {{ endpointStatusCode }} /
          {{ queuePosition }} / {{ queuePositionChangeReason }} /

          <fieldset>
            <legend>Set endpoint status:</legend>

            <div
              v-for="pair in entries(EndpointStatus).slice(
                entries(EndpointStatus).length / 2
              )"
              :key="pair"
            >
              <input
                v-model="testEndpointStatusCode"
                :id="pair[0]"
                :value="pair[1]"
                type="radio"
                name="test_endpoint_status_code"
              />
              <label for="test_endpoint_status_code">{{ pair[0] }}</label>
            </div>
          </fieldset>
          <fieldset>
            <legend>Set queue position:</legend>

            <div v-for="position of [-1, 0, 1]" :key="position">
              <input
                v-model="testQueuePosition"
                :id="`test_queue_position_${position}`"
                :value="position"
                type="radio"
                name="test_queue_position"
              />
              <label for="test_queue_position">{{ position }}</label>
            </div>
          </fieldset>
          <fieldset>
            <legend>Set queue position change reason:</legend>

            <div
              v-for="pair in entries(CallQueuePositionChangedReason).slice(
                entries(CallQueuePositionChangedReason).length / 2
              )"
              :key="pair"
            >
              <input
                v-model="testQueuePositionChangeReason"
                :id="`test_queue_position_change_reason_${pair[1]}`"
                :value="pair[1]"
                type="radio"
                name="test_queue_position_change_reason"
              />
              <label :for="`test_queue_position_change_reason_${pair[1]}`">{{
                pair[0]
              }}</label>
            </div>
          </fieldset>
          <fieldset>
            <legend>Set failed acquire request reason:</legend>

            <div v-for="v of AcquireFailedRequestReason" :key="v">
              <template v-if="+v in AcquireFailedRequestReason">
                <input
                  v-model="testAcquireFailedRequestReason"
                  :id="
                    `test_acquire_failed_request_reason_${AcquireFailedRequestReason[v]}`
                  "
                  :value="v"
                  type="radio"
                  name="test_acquire_failed_request_reason"
                />
                <label
                  :for="
                    `test_acquire_failed_request_reason_${AcquireFailedRequestReason[v]}`
                  "
                  >{{ AcquireFailedRequestReason[v] }}</label
                >
              </template>
            </div>
          </fieldset>
        </div>
        <TopPanel v-if="appType !== ClientAppType.embedded" />

        <div
          v-if="networkStatus === 'shutdown'"
          class="shutdown-content-container layout_fullscreen"
        >
          <img
            class="ico-96"
            src="data:image/svg+xml;charset=utf-8,<svg width='96' height='96' viewBox='0 0 96 96' fill='none' xmlns='http://www.w3.org/2000/svg'><rect width='96' height='96' fill='white'/><path fill-rule='evenodd' clip-rule='evenodd' d='M8 18C8 12.4772 12.4772 8 18 8H78C83.5228 8 88 12.4772 88 18V48H84V26.5H12V67C12 70.3137 14.6863 73 18 73H59V77H18C12.4772 77 8 72.5228 8 67V18ZM63.5 67C63.5 58.9919 69.9919 52.5 78 52.5C86.0081 52.5 92.5 58.9919 92.5 67C92.5 75.0081 86.0081 81.5 78 81.5C69.9919 81.5 63.5 75.0081 63.5 67ZM78 49.5C68.335 49.5 60.5 57.335 60.5 67C60.5 76.665 68.335 84.5 78 84.5C87.665 84.5 95.5 76.665 95.5 67C95.5 57.335 87.665 49.5 78 49.5Z' fill='%23838999'/><path fill-rule='evenodd' clip-rule='evenodd' d='M79.5 56V67H76.5V56H79.5ZM74.3448 61.6242C72.6244 62.7968 71.5 64.7673 71.5 67.0001C71.5 70.5899 74.4101 73.5001 78 73.5001C81.5899 73.5001 84.5 70.5899 84.5 67.0001C84.5 64.7673 83.3756 62.7968 81.6552 61.6242L83.3448 59.1452C85.85 60.8527 87.5 63.7335 87.5 67.0001C87.5 72.2468 83.2467 76.5001 78 76.5001C72.7533 76.5001 68.5 72.2468 68.5 67.0001C68.5 63.7335 70.15 60.8527 72.6552 59.1452L74.3448 61.6242Z' fill='%23DE0000'/></svg>"
          />
          <h2>{{ appName }} App: Unavailable!</h2>
          <p>
            This caller application has been closed because it is currently
            active in another browser window or tab.
          </p>
        </div>
        <TicketForm
          v-else-if="
            incident?.in_process &&
              getProperty('ticket.creation.enabled') &&
              !isWebKiosk
          "
        />
        <TicketCreationRequest
          v-else-if="
            incident &&
              incident.type !== IncidentType.none &&
              getProperty('ticket.creation.enabled') &&
              !isWebKiosk
          "
        />
        <Call
          v-else-if="isCallScreenVisible"
          @toTopics="() => toTopics()"
          @idle="
            () => {
              handleInactivityTimerTrigger();
              toTopics();
            }
          "
          @redirect="redirect"
          @hangup="handleHangupRequestSent"
          :is-reconnecting-screen-visible="isReconnectingScreenVisible"
          :is-content-hidden="isCallScreenContentHidden"
        />
        <template
          v-else-if="
            [EndpointStatus.unknown, EndpointStatus.ready, null].includes(
              endpointStatusCode
            )
          "
        >
          <Topics @idle="handleInactivityTimerTrigger" />
        </template>
        <div v-else class="main-spinner-container">
          <Spinner class="main-spinner" />
        </div>
      </template>
    </main>
    <div v-else class="main-spinner-container">
      <Spinner class="main-spinner" />
    </div>
  </template>

  <div v-if="!isOnline" class="mdr-warning-backdrop">
    <div class="mdr-warning borderless danger middle">
      <div class="body">
        <img
          src="data:image/svg+xml;charset=utf-8,<svg width='128' height='128' viewBox='0 0 128 128' fill='none' xmlns='http://www.w3.org/2000/svg'><path opacity='0.5' d='M81.6496 30.9567C75.9633 24.2539 67.4784 20 58 20C41.8911 20 28.652 32.287 27.1433 48.0004C27.0955 48.0001 27.0478 48 27 48C14.2975 48 4 58.2975 4 71C4 83.7025 14.2975 94 27 94H38.3073L81.6496 30.9567Z' fill='%238F929B'/><path d='M84.5085 29.7074C78.2807 21.9638 68.7201 17 58 17C41.2277 17 27.2947 29.1424 24.5074 45.1179C11.3173 46.3725 1 57.4814 1 71C1 85.3594 12.6406 97 27 97H38.2448L42.3698 91H27C15.9543 91 7 82.0457 7 71C7 59.9543 15.9543 51 27 51C27.0416 51 27.0833 51.0001 27.1249 51.0004L29.8674 51.0172L30.1295 48.2872C31.4919 34.0976 43.4518 23 58 23C67.4308 23 75.7735 27.6596 80.8518 34.8156L80.9253 34.9192L84.5085 29.7074Z' fill='%238F929B'/><path d='M54.5051 91H101C112.046 91 121 82.0457 121 71C121 60.4712 112.862 51.8403 102.536 51.0579L100.183 50.8797L99.8017 48.5517C98.9903 43.6016 95.7523 39.4668 91.3504 37.4069L94.7835 32.4132C99.832 35.0762 103.663 39.7362 105.228 45.3425C117.578 47.3632 127 58.08 127 71C127 85.3594 115.359 97 101 97H50.3801L54.5051 91Z' fill='%238F929B'/><path fill-rule='evenodd' clip-rule='evenodd' d='M91.8326 10.8798C94.1082 12.4442 94.6846 15.5571 93.1202 17.8326L27.1202 113.833C25.5558 116.108 22.4429 116.685 20.1673 115.12C17.8918 113.556 17.3153 110.443 18.8798 108.167L84.8798 12.1673C86.4442 9.8918 89.5571 9.31534 91.8326 10.8798Z' fill='%23DE0000'/></svg>"
          class="ico-192"
        />
        <h3>{{ $t("error.network.title") }}</h3>
      </div>
    </div>
  </div>

  <span v-if="appVersion" class="pt-app-version">
    {{ appVersion }}
  </span>
</template>

<script lang="ts">
import { useStore } from "vuex";
import { computed, onMounted, ref, watch } from "vue";
import { showToastError } from "@/helpers/notification";
import Call from "@/components/Call.vue";
import Spinner from "@/components/Spinner.vue";
import moment from "moment";
import {
  CallQueuePositionChangedReason,
  Config,
  EndpointStatus,
  IncidentDTO,
  IncidentType,
  MessageType,
  RemSignal,
  StatisticMode,
  TelephonyType,
  WebrtcPhoneState,
  ClientAppType,
  AuthorizationFailedReason,
  AcquireFailedRequestReason,
  RequestedAction,
  WebexAccessToken,
  ProfileDto
} from "@moderrotech/servers-communication-library/src/types";
import { i18n } from "@/lang";
import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import Topics from "@/components/Topics.vue";
import { useRoute, useRouter } from "vue-router";
import { setLocale } from "@vee-validate/i18n";
import TicketCreationRequest from "@/components/TicketCreationRequest.vue";
import TicketForm from "@/components/TicketForm.vue";
import TopPanel from "@/components/TopPanel.vue";
import { sendParentWindowMessage } from "@/helpers/embedded";
import { useIntervalFn, useLocalStorage, useCssVar } from "@vueuse/core";
import {
  clearNotifications,
  getDeviceInfo,
  getNotificationId,
  initApp,
  getRootUrl
} from "@moderrotech/servers-communication-library/src/helpers/cordova";
import { getMatchedLocale } from "@/validation/locales";
import { useWebSocket } from "@vueuse/core";
import { useTitle, useFavicon } from "@vueuse/core";
import axios from "axios";
import { useOnline } from "@vueuse/core";
import ErrorScreen from "@/components/ErrorScreen.vue";
import { preloadAvatarGlb } from "@/helpers/avatar";

export default {
  components: {
    ErrorScreen,
    TopPanel,
    TicketForm,
    TicketCreationRequest,
    Topics,
    Spinner,
    Call
  },
  setup() {
    const store = useStore();

    const router = useRouter(),
      route = useRoute();

    const isKioskType = computed(() => store.getters["auth/isKioskType"]),
      isKioskOs = computed(() => store.getters["auth/isKioskOs"]),
      isWebKiosk = computed(() => store.getters["auth/isWebKiosk"]);

    const config = computed((): Config => store.state.config.config);

    const profile = computed((): ProfileDto | null => store.state.auth.profile),
      appType = computed(() => profile.value?.app?.type);

    watch(
      () => isKioskType.value,
      v => {
        if (v) {
          _.each(
            _.pick(config.value, [
              "topicButtonBackgroundColor",
              "topicButtonBorderColor",
              "topicButtonTextColor",
              "topicButtonShadowColor",
              "topicButtonTextAlign",
              "topicMenuDirection",
              "appTextColor",
              "topicsPageTitleTextAlign",
              "sessionPageBrandingBackgroundColor",
              "sessionPageBrandingTextColor"
            ]),
            (v, k) => (useCssVar(`--${k}`).value = v)
          );

          const recalcSize = (px: number) =>
            `${(px / 1920) * 100}${
              appType.value === ClientAppType.kioskLandscape ? "vw" : "dvh"
            }`;

          _.each(
            _.pickBy(
              config.value,
              (v, k) =>
                _.includes(
                  [
                    "topicButtonBorderWidth",
                    "topicButtonHeight",
                    "topicButtonWidth",
                    "topicButtonRadius",
                    "topicButtonFontSize",
                    "topicMenuSpacing",
                    "topicsPageTitleWidth",
                    "topicsPageTitleHeight",
                    "topicsPageTitleFontSize",
                    "topicsPageLogoWidth",
                    "topicsPageLogoHeight",
                    "sessionPageBackgroundBlur"
                  ],
                  k
                ) && v !== null
            ),
            (v, k) => (useCssVar(`--${k}`).value = recalcSize(v))
          );

          _.each(["topicsPageTitleWidth", "topicsPageTitleHeight"], v => {
            if ((config.value?.[v] ?? null) === null) {
              useCssVar(`--${v}`).value = "auto";
            }
          });

          _.each(
            _.pick(config.value, [
              "topicMenuPosition",
              "topicsPageTitlePosition",
              "topicsPageTitlePosition",
              "topicsPageLogoPosition"
            ]),
            (v, k) =>
              _.each(
                ["top", "left", "bottom", "right"],
                p =>
                  (useCssVar(`--${k}${_.upperFirst(p)}`).value =
                    (v[p] ?? null) !== null ? recalcSize(v[p]) : "auto")
              )
          );
        }
      },
      { immediate: true }
    );

    const isReady = ref(false),
      isAppUnpublished = ref(false),
      authorizationFailedReason = ref<AuthorizationFailedReason | null>(null);

    const testEndpointStatusCode = ref();
    watch(testEndpointStatusCode, (v: EndpointStatus) =>
      store.commit("SET_ENDPOINT_STATUS", {
        details: _.startCase(
          _.find(
            _.entries(EndpointStatus).slice(
              _.entries(EndpointStatus).length / 2
            ),
            pair => pair[1] === v
          )?.[0]
        ),
        extension: "2012",
        parties: [],
        status: v
      })
    );

    const testQueuePosition = ref();
    watch(testQueuePosition, (position: number) =>
      store.commit("queue/SET_POSITION", position)
    );

    const testQueuePositionChangeReason = ref();
    watch(
      testQueuePositionChangeReason,
      (reason: CallQueuePositionChangedReason) =>
        store.commit("queue/SET_POSITION_CHANGE_REASON", reason)
    );

    const testAcquireFailedRequestReason = ref();
    watch(
      testAcquireFailedRequestReason,
      (reason: AcquireFailedRequestReason) =>
        store.commit("queue/SET_ACQUIRE_FAILED_REQUEST_REASON", reason)
    );

    const queuePosition = computed(() => store.state.queue.position),
      queuePositionChangeReason = computed(
        () => store.state.queue.positionChangeReason
      );

    const endpointStatusCode = computed(() => store.getters.endpointStatusCode),
      prevEndpointStatusCode = ref<EndpointStatus | null>(null);

    const isVisible = ref<boolean>(!document.hidden);

    watch(
      () => [
        endpointStatusCode.value === EndpointStatus.connected,
        isVisible.value
      ],
      ([v1, v2]) => {
        if (!v1 || typeof v2 !== "boolean") {
          return;
        }
        store.dispatch("session/sendJsonMessage", {
          message: {
            type: v2 ? MessageType.appShown : MessageType.appHidden
          }
        });
      },
      { immediate: true }
    );

    onMounted(() => {
      if (typeof document.addEventListener === "undefined") {
        return;
      }

      document.addEventListener(
        "visibilitychange",
        () => (isVisible.value = !document.hidden),
        false
      );
    });

    watch(
      () => endpointStatusCode.value,
      async (newVal, oldVal) => {
        if ((oldVal ?? null) !== null) {
          prevEndpointStatusCode.value = oldVal;
        }
      },
      { immediate: true }
    );

    const closeTabConfirmation = (e: BeforeUnloadEvent) => {
      e = e || window.event;

      const message = "Are you really sure to close/reload the page?";

      // For IE and Firefox prior to version 4
      if (e) {
        e.returnValue = message;
      }

      // For Safari
      return message;
    };

    const isCallScreenVisible = computed(
      () =>
        (route.name === "thank" &&
          [EndpointStatus.unknown, EndpointStatus.ready, null].includes(
            endpointStatusCode.value
          )) ||
        (queuePosition.value !== null && queuePosition.value >= 0) ||
        [
          EndpointStatus.transferring,
          EndpointStatus.placingCall,
          EndpointStatus.connected,
          EndpointStatus.failure
        ].includes(endpointStatusCode.value) ||
        (endpointStatusCode.value === EndpointStatus.ready &&
          prevEndpointStatusCode.value &&
          [EndpointStatus.connected, EndpointStatus.transferring].includes(
            prevEndpointStatusCode.value
          )) ||
        store.getters["queue/isThrownUrgently"] ||
        route.query.topic ||
        route.query.room_id
    );

    const defaultAppLocale = ref<string | null>(null),
      handleInactivityTimerTrigger = () => {
        if ((isKioskOs.value || isWebKiosk.value) && defaultAppLocale) {
          store.commit("config/PATCH_CONFIG", { appLocale: defaultAppLocale });
        }
      };

    const connectToSidekickApp = () => {
      const { status, send } = useWebSocket("ws://127.0.0.1:8080", {
        onConnected: () => console.log("Connected to Sidekick App"),
        onDisconnected: () => console.log("Disconnected from Sidekick App"),
        onMessage: (ws: WebSocket, event: MessageEvent) => {
          const data = JSON.parse(event.data);

          console.log("Sidekick App message received: ", data);
          store.dispatch("session/sendJsonMessage", {
            message: {
              type: MessageType.sidekickAppMessage,
              payload: data
            }
          });
        },
        autoReconnect: {
          retries: 3,
          delay: 1000,
          onFailed: () => console.error("Can not connect to Sidekick App")
        }
      });

      watch(
        () => [
          status.value === "OPEN",
          _.filter(store.state.session.requestedActions, {
            action: RequestedAction.sendSidekickAppMessage
          })
        ],
        ([v1, v2]) => {
          if (
            v1 &&
            (v2 as { action: RequestedAction; payload: object }[])?.length
          ) {
            _.forEach(
              (v2 ?? []) as { action: RequestedAction; payload: object }[],
              a => {
                const data = JSON.stringify(a.payload);
                console.log("Sending data to Sidekick App: ", data);
                send(data);
              }
            );
            store.commit(
              "session/UNSET_REQUESTED_ACTION",
              RequestedAction.sendSidekickAppMessage
            );
          }
        },
        { immediate: true }
      );
    };

    const removeClosingConfirmation = () =>
        window.removeEventListener("beforeunload", closeTabConfirmation),
      reloadPage = () => {
        console.log("Reloading page...");
        removeClosingConfirmation();
        window.location.reload();
      };

    const networkStatus = computed(() => store.state.network.status);

    watch(
      () => networkStatus.value,
      async v => {
        if (v === "shutdown" && route.name !== "shutdown") {
          removeClosingConfirmation();
          await router.replace({
            name: "shutdown"
          });
          window.location.reload();
        }
      }
    );

    onMounted(async () => {
      await router.isReady();

      if (route.name !== "shutdown") {
        connectToSidekickApp();
      }

      if (route.query.kiosk_id) {
        store.commit("auth/SET_KIOSK_ID", route.query.kiosk_id);
        await router.replace({
          name: route.name!,
          params: route.params,
          query: _.omit(route.query, "kiosk_id")
        });
      }

      const kioskId = store.state.auth.kioskId;

      if (kioskId) {
        store.commit("metadata/UNSET_CUSTOMER_INFO");
      }

      let config;

      const timerInterval = 1 * 60 * 1000; // 1 minute

      const { pause: pauseConfigRetrievingTimer } = useIntervalFn(
        async () => {
          try {
            config = await store.dispatch("config/getConfig", {
              isTemplatesSupported: true
            });

            defaultAppLocale.value =
              store.state.config.config.appLocale ?? null;

            pauseConfigRetrievingTimer();
            isAppUnpublished.value = false;
          } catch (err) {
            isAppUnpublished.value = true;

            if (!kioskId) {
              pauseConfigRetrievingTimer(); // retry only if app launched on kiosk
            }

            return;
          }

          const supportedLocales = _.keys(
              _.pickBy(
                store.state.config.config.appLocalesOrder ?? {},
                l => l >= 0
              )
            ),
            storedLocale = useLocalStorage("app.locale", null);

          if (!_.includes(supportedLocales, storedLocale.value)) {
            storedLocale.value = null;
          }

          const browserLocale = _.first(_.split(navigator.language, "-")),
            locale =
              storedLocale.value ??
              (!kioskId &&
              browserLocale &&
              _.includes(supportedLocales, browserLocale)
                ? browserLocale
                : store.state.config.config.appLocale);

          const cameraLabel = (route.query.camera_label ?? null) as
            | string
            | null;

          if (cameraLabel) {
            // used for app opened on kiosk

            store.commit("session/mediaDevices/SET_FORCED_CAMERA", cameraLabel);

            await router.replace({
              name: route.name!,
              params: route.params,
              query: _.omit(route.query, "camera_label")
            });
          }

          const extraConfig = _.assign(
            _.omit(
              {
                publicPath: process.env.VUE_APP_PUBLIC_PATH
                  ? `/${_.trim(process.env.VUE_APP_PUBLIC_PATH, "/")}/`
                  : "/",
                clientType: "kiosk"
              },
              _.keys(config)
            ),
            locale !== store.state.config.config.appLocale
              ? { appLocale: locale }
              : {}
          );

          store.commit("config/PATCH_CONFIG", extraConfig);

          let indexHtml: string;

          useIntervalFn(
            async () => {
              console.log("Retrieving index.html file content...");

              const html = (
                await axios.get(store.state.config.config.publicPath, {
                  params: { timestamp: moment().unix() }
                })
              ).data;

              console.log("Retrieved index.html file content: ", {
                html: _.truncate(html, {
                  length: 100
                })
              });

              if (!indexHtml) {
                indexHtml = html;
              } else if (indexHtml !== html) {
                console.log(
                  "index.html file content was changed. App new build was uploaded"
                );
                reloadPage();
              }
            },
            60 * 1000,
            {
              immediateCallback: true
            }
          );

          if (store.state.config.config?.webPageCustomTitle) {
            useTitle(store.state.config.config?.webPageCustomTitle as string);
          }
          if (store.state.config.config?.customFavicon) {
            useFavicon(store.state.config.config?.customFavicon as string);
          }

          const deviceId =
            kioskId ?? store.state.auth.deviceId ?? `{${uuidv4()}}`;
          store.commit("auth/SET_DEVICE_ID", deviceId);

          if (route.name === "shutdown") {
            store.commit("network/SET_STATUS", "shutdown");
            isReady.value = true;
            return;
          }

          store.commit("auth/UNSET_JWT");

          const { pause: pauseLoginTimer } = useIntervalFn(
            async () => {
              try {
                await store.dispatch("auth/login", {
                  id: profile.value?.id
                });
                pauseLoginTimer();
                authorizationFailedReason.value = null;
              } catch (err) {
                authorizationFailedReason.value = _.get(
                  err,
                  "response.data.data.reason",
                  AuthorizationFailedReason.noPtpcResponse
                ) as AuthorizationFailedReason;

                if (!kioskId) {
                  pauseLoginTimer(); // retry only if app launched on kiosk
                }

                return;
              }

              if (isKioskOs.value) {
                store.dispatch("arecorder/init", {
                  callback: () => {
                    /* empty */
                  },
                  errorCallback: (): void =>
                    showToastError("Can not init arecorder")
                });
              } else {
                const cordovaRoot = useLocalStorage(
                  "cordova.root",
                  route.query.cordova_root ?? ""
                );

                if (cordovaRoot.value) {
                  const rootUrl = getRootUrl(
                    cordovaRoot.value as string,
                    `${window.location.origin}${store.state.config.config
                      ?.publicPath ?? ""}`
                  );

                  await router.replace({
                    name: route.name!,
                    params: route.params,
                    query: _.omit(route.query, "cordova_root")
                  });

                  try {
                    await initApp({
                      rootUrl,
                      screenOrientation:
                        appType.value === ClientAppType.kioskLandscape
                          ? "landscape"
                          : "portrait",
                      callbacks: {
                        answer: () => store.dispatch("session/answer"),
                        hangup: () => store.dispatch("session/hangup")
                      }
                    });
                    store.commit("config/PATCH_CONFIG", {
                      mobileAppData: {
                        // eslint-disable-next-line @typescript-eslint/camelcase
                        notification_id: getNotificationId(),
                        platform: getDeviceInfo()?.platform
                      }
                    });
                    clearNotifications();
                  } catch (err) {
                    console.error("Can not init Cordova mobile app: ", err);
                  }
                }
              }

              watch(
                () => store.state.config.config.appLocale,
                (newVal, oldVal) => {
                  if (newVal) {
                    i18n.global.locale = newVal;
                    setLocale(getMatchedLocale(newVal));

                    moment.locale(_.toLower(_.replace(newVal, "_", "-")));

                    if (oldVal) {
                      // changed manually
                      storedLocale.value = newVal;
                    }
                  }
                },
                { immediate: true }
              );

              _.forEach(["male", "female"], g =>
                preloadAvatarGlb(
                  `${store.state.config.config.publicPath}avatars/${g}-merged.glb`
                )
              );

              store.dispatch("network/setCallbacks", {
                onTimedOut: () => {
                  console.log(
                    "Network connection timed out, reloading web page..."
                  );
                  reloadPage();
                }
              });

              store.dispatch("incident/get", {
                callback: () => {
                  store.dispatch("getTelephonyType", {
                    callback: (telType: TelephonyType) => {
                      if (telType === TelephonyType.webex) {
                        const cb = () =>
                          store.dispatch("webex/init", {
                            createMeeting: true
                          });

                        if (
                          !store.state.webex.accessToken ||
                          store.state.webex.accessToken?.expires <=
                            moment().unix()
                        ) {
                          store.dispatch("webex/getAccessToken", {
                            callback: (token: WebexAccessToken) => cb(),
                            errorCallback: (): void =>
                              showToastError("Can not get Webex access token")
                          });
                        } else {
                          cb();
                        }
                      }
                      store.dispatch("signals/subscribe", {
                        callback: () => {
                          isReady.value = true;
                          store.dispatch("topics/getList", {
                            callback: async () => {
                              if (telType === TelephonyType.twilio) {
                                store.dispatch("setEndpointStatus", {
                                  status: EndpointStatus.ready
                                });
                              }
                              store.dispatch("getEndpointStatus");
                              store.dispatch("queue/getPosition", {
                                callback: () => store.dispatch("getHints")
                              });
                              if (telType === TelephonyType.webrtc) {
                                store.dispatch("webrtc/getPhoneState");
                              } else if (telType === TelephonyType.zoom) {
                                await store.dispatch("zoom/init");
                              }
                            }
                          });
                        }
                      });
                    }
                  });
                }
              });
            },
            timerInterval,
            {
              immediateCallback: true
            }
          );
        },
        timerInterval,
        {
          immediateCallback: true
        }
      );
    });

    watch(
      () => isCallScreenVisible.value,
      v => {
        if (v) {
          window.addEventListener("beforeunload", closeTabConfirmation);
        } else {
          window.removeEventListener("beforeunload", closeTabConfirmation);
        }
      }
    );

    watch(
      () =>
        route.name === "thank" &&
        ![EndpointStatus.unknown, EndpointStatus.ready, null].includes(
          endpointStatusCode.value
        ),
      v => {
        if (v) {
          router.replace({
            name: "app",
            params: route.params,
            query: _.pick(route.query, ["login_type"])
          });
        }
      }
    );

    const telephonyType = computed(() => store.state.telephonyType);

    const feedbackPayload = ref<{
      session?: {
        id?: string;
        started_at?: string;
      };
      // expert?: {
      //   extension: string;
      // };
      // client?: {
      //   extension: string;
      // };
    }>({});

    const toTopics = async () => {
      prevEndpointStatusCode.value = null;
      await router.replace({
        name: "app",
        params: route.params,
        query: _.pick(route.query, ["login_type"])
      });
      feedbackPayload.value = {};
      _.each(
        ["UNSET_POSITION_CHANGE_REASON", "UNSET_ACQUIRE_FAILED_REQUEST_REASON"],
        v => store.commit(`queue/${v}`)
      );
      if (endpointStatusCode.value === EndpointStatus.failure) {
        reloadPage();
      }
    };

    watch(
      () =>
        store.state.queue.positionChangeReason ===
          CallQueuePositionChangedReason.released &&
        endpointStatusCode.value !== EndpointStatus.failure,
      v => {
        if (v) {
          toTopics();
        }
      },
      { immediate: true }
    );

    const getProperty = (name: string) => store.getters["auth/property"](name);

    const getRedirectionUrl = (): string | null =>
        route.query.redirect ?? getProperty("after.call.redirect.url") ?? null,
      redirect = () => {
        const url = getRedirectionUrl();

        if (!url) {
          return;
        }

        const cb = () => {
          window.removeEventListener("beforeunload", closeTabConfirmation);

          if (telephonyType.value === TelephonyType.webex) {
            store.dispatch("webex/leaveMeeting", {
              callback: () => (window.location.href = url),
              errorCallback: () => (window.location.href = url)
            });
          } else {
            window.location.href = url;
          }
        };

        store.dispatch("queue/release", {
          callback: cb,
          errorCallback: cb
        });
      };

    const hangupRequestSentAt = ref<number | null>(null),
      handleHangupRequestSent = () => {
        if (getRedirectionUrl()) {
          redirect();
          return;
        }
        hangupRequestSentAt.value = +moment();
      };

    watch(
      () =>
        !!getRedirectionUrl() &&
        store.state.session.lastRequest?.signal === RemSignal.hangupRequested,
      val => {
        if (val) {
          redirect();
        }
      }
    );

    const isTelephonySubordinate = computed(
      () => store.getters.isTelephonySubordinate
    );

    watch(
      () =>
        !hangupRequestSentAt.value &&
        prevEndpointStatusCode.value === EndpointStatus.placingCall &&
        endpointStatusCode.value === EndpointStatus.ready &&
        !isTelephonySubordinate.value, // not for mustang
      val => {
        if (val) {
          // expert did not answer - show 'no experts' error page
          store.dispatch("queue/getPosition", {
            callback: () => {
              if (queuePosition.value < 0) {
                store.commit(
                  "queue/SET_POSITION_CHANGE_REASON",
                  CallQueuePositionChangedReason.noExperts
                );
              }
            }
          });
        }
      }
    );

    watch(
      () =>
        !hangupRequestSentAt.value &&
        prevEndpointStatusCode.value === EndpointStatus.transferring &&
        endpointStatusCode.value === EndpointStatus.ready,
      val => {
        if (val) {
          // call was transferred, but expert did not answer - show 'no experts' error page
          store.dispatch("queue/getPosition", {
            callback: () => {
              if (queuePosition.value < 0) {
                store.commit(
                  "queue/SET_POSITION_CHANGE_REASON",
                  CallQueuePositionChangedReason.noExperts
                );
              }
            }
          });
        }
      }
    );

    watch(
      () =>
        !!hangupRequestSentAt.value &&
        prevEndpointStatusCode.value === EndpointStatus.placingCall &&
        endpointStatusCode.value === EndpointStatus.ready,
      val => {
        if (val) {
          if (getRedirectionUrl()) {
            redirect();
            return;
          }

          // client clicked 'cancel call' button before expert's answer - show topics page
          hangupRequestSentAt.value = null;
          toTopics();
        }
      }
    );

    watch(
      () => [!!hangupRequestSentAt.value, queuePosition.value],
      (
        [newIsHangupRequestSent, newQueuePosition],
        [oldIsHangupRequestSent, oldQueuePosition]
      ) => {
        if (
          [EndpointStatus.ready, EndpointStatus.unknown, null].includes(
            endpointStatusCode.value
          ) &&
          newIsHangupRequestSent &&
          oldIsHangupRequestSent &&
          newQueuePosition < 0 &&
          oldQueuePosition >= 0
        ) {
          if (getRedirectionUrl()) {
            redirect();
            return;
          }

          // client clicked 'call' button before telephony initialization and 'cancel call' one before expert's answer - show topics page
          hangupRequestSentAt.value = null;
          toTopics();
        }
      }
    );

    const sessionInfo = computed(() => store.state.session.info);

    watch(
      () => endpointStatusCode.value === EndpointStatus.connected,
      async val => {
        if (val) {
          feedbackPayload.value.session = _.assign(
            {},
            feedbackPayload.value.session ?? {},
            {
              // eslint-disable-next-line @typescript-eslint/camelcase
              started_at: moment()
                .utc()
                .format()
            }
          );
        }
      },
      { immediate: true }
    );

    watch(
      () => sessionInfo.value,
      v => {
        if (v?.id) {
          feedbackPayload.value.session = _.assign(
            {},
            feedbackPayload.value.session ?? {},
            { id: v.id as string }
          );
        }
        // if (v?.companion_endpoint_status?.extension) {
        //   feedbackPayload.expert = {
        //     extension: v.companion_endpoint_status.extension as string
        //   };
        // }
      },
      { immediate: true }
    );

    const isCallScreenContentHidden = ref(false);

    const redirectToThankPage = async () => {
      const isWebex = telephonyType.value === TelephonyType.webex;

      if (isWebex) {
        isCallScreenContentHidden.value = true;
      }

      await router.replace({
        name: "thank",
        params: route.params,
        query: _.assign(
          {
            // eslint-disable-next-line @typescript-eslint/camelcase
            feedback_payload: JSON.stringify(feedbackPayload.value)
          },
          _.pick(route.query, ["login_type"])
        )
      });

      if (isWebex) {
        reloadPage();
      }
    };

    watch(
      () =>
        prevEndpointStatusCode.value === EndpointStatus.connected &&
        endpointStatusCode.value === EndpointStatus.ready &&
        route.name !== "thank",
      async val => {
        if (val) {
          isCallScreenContentHidden.value = true;

          store.dispatch("queue/getPosition", {
            callback: async () => {
              isCallScreenContentHidden.value = false;

              if (queuePosition.value >= 0) {
                // transfer to another topic in progress
                return;
              }

              if (getRedirectionUrl()) {
                redirect();
                return;
              }

              // call ended properly (after establishing)

              // notify parent window about call finishing (if app opened in iframe)
              sendParentWindowMessage("call-ended");

              await redirectToThankPage();
            }
          });
        }
      }
    );

    watch(
      () =>
        prevEndpointStatusCode.value === EndpointStatus.transferring &&
        endpointStatusCode.value === EndpointStatus.ready &&
        route.name !== "thank",
      async val => {
        if (val) {
          if (hangupRequestSentAt.value) {
            // client cancelled call during a transfer
            await redirectToThankPage();
            return;
          }
          store.dispatch("queue/getPosition", {
            callback: () => {
              if (queuePosition.value < 0) {
                // expert did not answer - show 'no experts' error page
                store.commit(
                  "queue/SET_POSITION_CHANGE_REASON",
                  CallQueuePositionChangedReason.noExperts
                );
              } else {
                redirectToThankPage();
              }
            }
          });
        }
      }
    );

    const jwt = computed(() => store.state.auth.jwt);

    const isConsoleListening = computed(
      () => store.state.auth.isConsoleListening
    );

    const {
      pause: pauseLogStoreTimer,
      resume: resumeLogStoreTimer
    } = useIntervalFn(
      async () => {
        store.dispatch("log/store");
      },
      5 * 60 * 1000,
      {
        immediateCallback: false
      }
    );

    watch(
      () => !!jwt.value,
      async (newVal, oldVal) => {
        if (newVal && !oldVal && profile.value?.store_logs) {
          if (!isConsoleListening.value) {
            store.dispatch("log/listenConsole");
          }
          resumeLogStoreTimer();
        } else if (!newVal && oldVal) {
          if (isConsoleListening.value) {
            store.dispatch("log/stopConsoleListening");
          }
          pauseLogStoreTimer();
          store.commit("log/UNSET_LIST");
        }
      },
      { immediate: true }
    );

    let statisticTimer: number;
    watch(
      () => endpointStatusCode.value === EndpointStatus.connected,
      v => {
        if (v) {
          if (telephonyType.value !== TelephonyType.webex) {
            return;
          }

          const mode =
            getProperty("webex.statistics.mode") ?? StatisticMode.disabled;

          if (mode === StatisticMode.disabled) {
            return;
          }

          statisticTimer = window.setInterval(
            () => store.dispatch("session/setStatistic"),
            getProperty("webex.statistics.timeout") as number
          );
        } else {
          window.clearInterval(statisticTimer);
        }
      }
    );

    const appVersion = computed(() => process.env.VUE_APP_VERSION);

    onMounted(async () => {
      if (appVersion.value) {
        console.log("App version: ", appVersion.value);
      }
    });

    const isReconnectingScreenVisible = computed(
      () =>
        endpointStatusCode.value === EndpointStatus.connected &&
        (networkStatus.value === "disconnected" ||
          (telephonyType.value === TelephonyType.webrtc &&
            store.state.webrtc.phoneState?.state === WebrtcPhoneState.onCall &&
            store.state.webrtc.networkStatus === "disconnected"))
    );

    return {
      toTopics,
      handleInactivityTimerTrigger,
      handleHangupRequestSent,
      config,
      isReady,
      endpointStatusCode,
      prevEndpointStatusCode,
      testEndpointStatusCode,
      testQueuePosition,
      testQueuePositionChangeReason,
      testAcquireFailedRequestReason,
      queuePosition,
      queuePositionChangeReason,
      isCallScreenVisible,
      networkStatus,
      redirect,
      jwt,
      isCallScreenContentHidden,
      route,
      getProperty,
      isAppUnpublished,
      authorizationFailedReason,
      AuthorizationFailedReason,
      isReconnectingScreenVisible,
      isKioskType,
      EndpointStatus,
      CallQueuePositionChangedReason,
      incident: computed(
        (): IncidentDTO | null => store.state.incident.incident
      ),
      IncidentType,
      appType,
      appVersion,
      ClientAppType,
      AcquireFailedRequestReason,
      appName: process.env.VUE_APP_NAME,
      isWebKiosk,
      isOnline: useOnline(),
      ..._.pick(_, ["entries"])
    };
  }
};
</script>

<style lang="scss">
@import "toastr/toastr.scss";
@import "assets/styles/main";
@import "/assets/styles/breakpoints.module";
@import "/assets/styles/spinner.scss";

#app {
  .app-main {
    width: 100%;
    position: relative;
  }

  @font-face {
    font-weight: normal;
    font-family: "Roboto";
    font-style: normal;
    src: url("./assets/fonts/Roboto/Roboto-Regular.eot");
    src: url("./assets/fonts/Roboto/Roboto-Regular.eot?#iefix")
        format("embedded-opentype"),
      url("./assets/fonts/Roboto/Roboto-Regular.woff2") format("woff2"),
      url("./assets/fonts/Roboto/Roboto-Regular.woff") format("woff"),
      url("./assets/fonts/Roboto/Roboto-Regular.ttf") format("truetype"),
      url("./assets/fonts/Roboto/Roboto-Regular.svg#Roboto-Regular")
        format("svg");
    font-display: swap;
  }

  font-family: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
    "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",
    sans-serif;

  @font-face {
    font-family: "localRobotoCondensedMedium";
    src: url("./assets/fonts/Roboto/RobotoCondensed-Medium.ttf")
      format("truetype");
    font-weight: 500;
    font-style: normal;
  }
}

#toast-container {
  font-family: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
    "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",
    sans-serif;

  @media #{$iphone-xs-portrait} {
    top: 50% !important;
    right: 50% !important;
    left: auto !important;
    bottom: auto !important;
    transform: translate(50%, -50%);
  }
}

.mdr-test-buttons-container {
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 9;
  background: rgba(0, 255, 0, 0.3);

  &--rtl {
    left: auto;
    right: 0;
  }
}

.shutdown-content-container {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;

  padding: 30px;
  padding-bottom: 80px;

  & > * {
    width: 100%;
    max-width: 380px;

    margin-top: 7px;
    margin-bottom: 7px;

    padding-left: 20px;
    padding-right: 20px;
  }

  .ico-96,
  svg {
    display: inline-block;
    width: 96px;
    height: 96px;
    overflow: hidden;

    background: rgba(0, 0, 0, 0.1);

    //margin-bottom: 20px;

    padding: 0 !important;
  }

  h2 {
    font-size: 21px;
    font-weight: 600;
    text-align: center;
  }

  p {
    text-align: center;
    font-size: 18px;
    line-height: 1.25;
    font-weight: 400;
  }
}
.mdr-logout {
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;

  bottom: 10px;
  left: 10px;
  width: 44px;
  height: 44px;
  overflow: hidden;
  border-radius: 50%;
  border: none; //solid 1px #a5aab3;
  background: #476adc;
  color: white;
  box-shadow: 0 3px 9px rgba(91, 100, 116, 0.2);
  z-index: 1000;
  cursor: pointer;

  &:not(:disabled) {
    &:hover {
      background-color: scale-color(#476adc, $lightness: +5%); //#bbc7e2;
    }
    &:active {
      background-color: scale-color(#476adc, $lightness: -5%);
    }
  }
}

.mdr-hidden {
  z-index: -1;
  opacity: 0;
  width: 0;
  height: 0;
  overflow: hidden;
}

.mdr-app-logo-container {
  flex-grow: 0;
  flex-shrink: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;

  //padding-right: 30px;
  //padding-left: 30px;

  img,
  svg {
    object-fit: contain;
    width: 100%;
    height: auto;
    max-height: 290px;
  }
}

.mdr-btn-settings {
  position: fixed;
  left: 15px;
  top: 15px;

  width: 44px;
  height: 44px;
  //overflow: hidden;
  border: none;
  background-color: transparent;

  &_right {
    left: auto;
    right: 15px;
  }

  &::before {
    position: absolute;
    left: 2px;
    top: 2px;
    content: "";
    display: inline-block;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: hsla(0, 0%, 0%, 0.5);

    -webkit-backdrop-filter: blur(30px);
    -moz-backdrop-filter: blur(30px);
    backdrop-filter: blur(30px);
  }

  &::after {
    position: absolute;
    left: 4px;
    top: 4px;
    content: "";
    display: inline-block;
    width: 36px;
    height: 36px;
    border-radius: 50%;

    background-position: center center;
    background-repeat: no-repeat;
    background-size: 40px 40px;
    transition: opacity 0.2s, transform 0.2s;
    background-image: url("data:image/svg+xml;utf8, <svg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'><path fill-rule='evenodd' clip-rule='evenodd' d='M16.53 29.8071C16.1138 29.2958 15.745 28.7066 15.4275 28.0717C15.0576 27.3318 14.7401 26.4998 14.4837 25.5969C13.5064 25.7537 12.5565 25.9512 11.639 26.1861C12.861 27.8349 14.5591 29.1097 16.53 29.8071ZM10.7829 24.8218C10.8656 24.7577 10.9624 24.7088 11.07 24.6807C12.0529 24.4232 13.0701 24.2071 14.1158 24.0356C13.921 23.0138 13.7988 21.927 13.7594 20.8H9.63031C9.74007 22.2429 10.1443 23.6034 10.7829 24.8218ZM9.63031 19.2H13.7594C13.7988 18.073 13.921 16.9862 14.1158 15.9644C13.0701 15.7929 12.0529 15.5768 11.07 15.3193C10.9624 15.2912 10.8656 15.2423 10.7829 15.1782C10.1443 16.3966 9.74007 17.7571 9.63031 19.2ZM15.7023 23.8127C15.5188 22.873 15.4003 21.8612 15.3604 20.8H19.2V23.5721C18.0095 23.5974 16.8413 23.6789 15.7023 23.8127ZM19.2 25.1725C18.1407 25.196 17.1013 25.266 16.087 25.3791C16.3052 26.1056 16.5652 26.7694 16.8586 27.3561C17.5661 28.7711 18.3926 29.6108 19.2 29.9433V25.1725ZM20.8 29.9433V25.1725C21.8593 25.196 22.8987 25.266 23.913 25.3791C23.6948 26.1056 23.4348 26.7694 23.1414 27.3561C22.4339 28.7711 21.6074 29.6108 20.8 29.9433ZM20.8 23.5721V20.8H24.6396C24.5997 21.8612 24.4812 22.873 24.2977 23.8127C23.1587 23.6789 21.9905 23.5974 20.8 23.5721ZM20.8 19.2H24.6396C24.5997 18.1388 24.4812 17.127 24.2977 16.1873C23.1587 16.321 21.9905 16.4026 20.8 16.4279V19.2ZM20.8 14.8275C21.8593 14.804 22.8987 14.734 23.913 14.6209C23.6948 13.8944 23.4348 13.2306 23.1414 12.6439C22.4339 11.2289 21.6074 10.3892 20.8 10.0567V14.8275ZM19.2 10.0567V14.8275C18.1407 14.804 17.1013 14.734 16.087 14.6209C16.3052 13.8944 16.5652 13.2306 16.8586 12.6439C17.5661 11.2289 18.3926 10.3892 19.2 10.0567ZM19.2 16.4279V19.2H15.3604C15.4003 18.1388 15.5188 17.127 15.7023 16.1873C16.8413 16.321 18.0095 16.4026 19.2 16.4279ZM25.8842 24.0356C26.079 23.0138 26.2012 21.927 26.2406 20.8H30.3697C30.2599 22.2429 29.8557 23.6034 29.2171 24.8218C29.1344 24.7577 29.0376 24.7088 28.93 24.6807C27.9471 24.4232 26.9299 24.2071 25.8842 24.0356ZM26.2406 19.2H30.3697C30.2599 17.7571 29.8557 16.3966 29.2171 15.1782C29.1344 15.2423 29.0376 15.2912 28.93 15.3193C27.9471 15.5768 26.9299 15.7929 25.8842 15.9644C26.079 16.9862 26.2012 18.073 26.2406 19.2ZM25.5163 25.5969C25.2599 26.4998 24.9424 27.3318 24.5725 28.0717C24.255 28.7066 23.8862 29.2958 23.47 29.8071C25.4409 29.1097 27.139 27.8349 28.361 26.1861C27.4435 25.9512 26.4936 25.7537 25.5163 25.5969ZM25.5163 14.4031C26.4936 14.2463 27.4435 14.0488 28.361 13.8139C27.139 12.1651 25.4409 10.8903 23.47 10.1929C23.8862 10.7042 24.255 11.2934 24.5725 11.9283C24.9424 12.6682 25.2599 13.5002 25.5163 14.4031ZM11.639 13.8139C12.5565 14.0488 13.5064 14.2463 14.4837 14.4031C14.7401 13.5002 15.0576 12.6682 15.4275 11.9283C15.745 11.2934 16.1138 10.7042 16.53 10.1929C14.5591 10.8903 12.861 12.1651 11.639 13.8139ZM32 20C32 26.6274 26.6274 32 20 32C13.3726 32 8 26.6274 8 20C8 13.3726 13.3726 8 20 8C26.6274 8 32 13.3726 32 20Z' fill='white' fill-opacity='0.75'/></svg>");
  }

  transition: opacity 0.2s, transform 0.2s;

  &:not(:disabled) {
    opacity: 0.75;

    &:hover {
      opacity: 1;
    }

    &:active {
      opacity: 1;
      &::after {
        transform: scale(1.2);
      }
    }
  }

  &:disabled {
    opacity: 0.2;
  }
}

@import "@/assets/styles/kiosk-landscape-30inch/user-defined.scss";
@import "@/assets/styles/kiosk-portrait-30inch/user-defined.scss";
</style>
